import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Divider,
  Autocomplete,
  Alert,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  FormLabel
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Save as SaveIcon,
  Female as FemaleIcon,
  Male as MaleIcon,
  Transgender as TransgenderIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CustomDatePicker from './CustomDatePicker';
import { createApiUrl } from '../utils/apiConfig';
import { getAuthHeaders } from '../utils/authUtils';
import { useSnackbar } from './CentralSnackbar';

const AddPatient = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  
  // Form state - Patient Data
  const [patientData, setPatientData] = useState({
    firstName: '',
    lastName: '',
    patientId: '',
    birthDate: '',
    sex: ''
  });
  
  // Form state - Exam Data
  const [examData, setExamData] = useState({
    accessionNumber: '',
    admissionId: '',
    modality: '',
    studyDescription: '',
    clinicalInfo: '',
    question: '',
    referringPhysician: '',
    observationDate: '',
    firstReader: null,
    tenant: '1' // Default tenant
  });
  
  // Options for dropdowns
  const [studyDescriptions, setStudyDescriptions] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [referringPhysicians, setReferringPhysicians] = useState([]);
  const [examinations, setExaminations] = useState([]); // Store full examinations data
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');

  // Load dropdown options on mount
  useEffect(() => {
    loadDropdownData();
  }, []);

  // Filter study descriptions when modality changes
  useEffect(() => {
    if (examData.modality && examinations.length > 0) {
      // Filter examinations by selected modality
      const filteredExams = examinations.filter(e => e.modality === examData.modality);
      const filteredDescriptions = [...new Set(filteredExams.map(e => e.name).filter(Boolean))].sort();
      setStudyDescriptions(filteredDescriptions);
      
      // Clear study description ONLY if it doesn't match the new modality (not when user is typing)
      if (examData.studyDescription && !filteredDescriptions.includes(examData.studyDescription)) {
        // Check if this is from a modality change (not from user typing in study description)
        const isFromModalityChange = filteredDescriptions.length > 0;
        if (isFromModalityChange) {
          setExamData(prev => ({ ...prev, studyDescription: '' }));
        }
      }
    } else if (examinations.length > 0) {
      // Show all descriptions if no modality selected
      const allDescriptions = [...new Set(examinations.map(e => e.name).filter(Boolean))].sort();
      setStudyDescriptions(allDescriptions);
    }
  }, [examData.modality, examinations]); // Removed examData.studyDescription from dependencies

  const loadDropdownData = async () => {
    try {
      setLoadingData(true);
      
      // Load examinations for study descriptions and modalities
      const examinationsResponse = await fetch(createApiUrl('admin/examinations'), {
        headers: getAuthHeaders()
      });
      
      if (examinationsResponse.ok) {
        const examinationsData = await examinationsResponse.json();
        
        // Store full examinations for filtering
        setExaminations(examinationsData);
        
        // Extract unique study descriptions (alphabetically sorted)
        const uniqueDescriptions = [...new Set(examinationsData.map(e => e.name).filter(Boolean))].sort();
        setStudyDescriptions(uniqueDescriptions);
        
        // Extract unique modalities (alphabetically sorted)
        const uniqueModalities = [...new Set(examinationsData.map(e => e.modality).filter(Boolean))].sort();
        setModalities(uniqueModalities);
      }
      
      // Load users (group_level 1-5) for first_reader
      const usersResponse = await fetch(createApiUrl('users'), {
        headers: getAuthHeaders()
      });
      
      if (usersResponse.ok) {
        const allUsers = await usersResponse.json();
        // Filter group level 1-5 AND active users, then format for display
        const filteredUsers = allUsers
          .filter(u => u.group_level >= 1 && u.group_level <= 5 && u.is_active === true)
          .map(u => ({
            id: u.id,
            label: [u.academic_degree, u.first_name, u.last_name]
              .filter(Boolean)
              .join(' ') || u.username,
            username: u.username
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        
        setUsers(filteredUsers);
      }
      
      // Load tenants
      const tenantsResponse = await fetch(createApiUrl('admin/tenants'), {
        headers: getAuthHeaders()
      });
      
      if (tenantsResponse.ok) {
        const tenantsData = await tenantsResponse.json();
        setTenants(tenantsData.filter(t => t.is_active));
      }
      
      // Load referring physicians from addresses
      const physiciansResponse = await fetch(createApiUrl('referring-physicians'), {
        headers: getAuthHeaders()
      });
      
      if (physiciansResponse.ok) {
        const physiciansData = await physiciansResponse.json();
        setReferringPhysicians(physiciansData);
      }
      
    } catch (error) {
      console.error('Fehler beim Laden der Dropdown-Daten:', error);
      const errorMsg = 'Fehler beim Laden der Formulardaten';
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setLoadingData(false);
    }
  };

  const handlePatientDataChange = (field, value) => {
    setPatientData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleExamDataChange = (field, value) => {
    setExamData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateForm = () => {
    // Required patient fields
    if (!patientData.firstName || !patientData.lastName) {
      const errorMsg = 'Vorname und Nachname sind Pflichtfelder';
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    if (!patientData.birthDate) {
      const errorMsg = 'Geburtsdatum ist ein Pflichtfeld';
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    if (!patientData.sex) {
      const errorMsg = 'Geschlecht ist ein Pflichtfeld';
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    if (!patientData.patientId) {
      const errorMsg = 'Patienten-ID ist ein Pflichtfeld';
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    // Required exam fields
    if (!examData.modality) {
      const errorMsg = 'Modalität ist ein Pflichtfeld';
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    if (!examData.studyDescription) {
      const errorMsg = 'Studienbeschreibung ist ein Pflichtfeld';
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    if (!examData.observationDate) {
      const errorMsg = 'Untersuchungsdatum ist ein Pflichtfeld';
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    if (!examData.referringPhysician) {
      const errorMsg = 'Zuweiser ist ein Pflichtfeld';
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    if (!examData.accessionNumber || !examData.admissionId) {
      const errorMsg = 'Accession Number und Aufnahme-ID sind Pflichtfelder';
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Prepare data for backend
      // Handle firstReader: can be object with id, object without id (freeSolo), or null
      let firstReaderId = null;
      if (examData.firstReader) {
        if (typeof examData.firstReader === 'object' && examData.firstReader.id) {
          firstReaderId = examData.firstReader.id;
        }
        // If freeSolo text without id, we send null (no valid user selected)
      }
      
      const payload = {
        // Patient data
        patient_id: patientData.patientId,
        first_name: patientData.firstName,
        last_name: patientData.lastName,
        birth_date: patientData.birthDate,
        sex: patientData.sex,
        
        // Exam data
        accession_number: examData.accessionNumber,
        admission_id: examData.admissionId,
        modality: examData.modality,
        study_description: examData.studyDescription,
        clinical_info: examData.clinicalInfo,
        question: examData.question,
        referring_physician: examData.referringPhysician,
        observation_date: examData.observationDate,
        first_reader: firstReaderId,
        tenant: examData.tenant
      };
      
      const response = await fetch(createApiUrl('new-exams/manual'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        showSuccess('Patient erfolgreich angelegt');
        
        // MTR sollen NICHT zur Befundung weitergeleitet werden!
        // Stattdessen: Formular zurücksetzen für nächsten Patienten
        setPatientData({
          firstName: '',
          lastName: '',
          patientId: '',
          birthDate: '',
          sex: ''
        });
        
        setExamData({
          accessionNumber: '',
          admissionId: '',
          modality: '',
          studyDescription: '',
          clinicalInfo: '',
          question: '',
          referringPhysician: '',
          observationDate: '',
          firstReader: null,
          tenant: examData.tenant // Mandant beibehalten
        });
        
        // Scroll nach oben für bessere UX (der main-Container scrollt, nicht das Dokument)
        document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setError(data.message || 'Fehler beim Anlegen des Patienten');
        showError(data.message || 'Fehler beim Anlegen des Patienten');
      }
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      setError('Netzwerkfehler beim Anlegen des Patienten');
      showError('Netzwerkfehler beim Anlegen des Patienten');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, margin: '0 auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <PersonAddIcon sx={{ fontSize: 40, color: 'var(--rd-primary)' }} />
        <Typography variant="h4" sx={{ color: 'var(--rd-primary)', fontWeight: 'bold' }}>
          {t('addPatient.title', 'Patient manuell anlegen')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Two Column Layout */}
      <Grid container spacing={3}>
        {/* Left Column - Patient Data */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 3, color: 'var(--rd-primary)', fontWeight: 'bold' }}>
              {t('addPatient.patientData', 'Patientendaten')}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Patienten-ID* */}
              <TextField
                fullWidth
                label={t('addPatient.patientId', 'Patienten-ID')}
                value={patientData.patientId}
                onChange={(e) => handlePatientDataChange('patientId', e.target.value)}
                required
                className="textfield"
              />
              
              {/* Vorname, Nachname* */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label={t('addPatient.firstName', 'Vorname')}
                  value={patientData.firstName}
                  onChange={(e) => handlePatientDataChange('firstName', e.target.value)}
                  required
                  className="textfield"
                />
                <TextField
                  fullWidth
                  label={t('addPatient.lastName', 'Nachname')}
                  value={patientData.lastName}
                  onChange={(e) => handlePatientDataChange('lastName', e.target.value)}
                  required
                  className="textfield"
                />
              </Box>

              {/* Geburtsdatum* */}
              <CustomDatePicker
                label={t('addPatient.birthDate', 'Geburtsdatum')}
                value={patientData.birthDate}
                onChange={(e) => handlePatientDataChange('birthDate', e.target.value)}
                required
                sx={{ 
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    height: '90px',
                    minHeight: '40px',
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8.5px 14px',
                    height: '23px',
                  }
                }}
              />
              {/* Geschlecht */}
              <Box>
                <FormLabel required sx={{ mb: 1, display: 'block', fontSize: '0.875rem' }}>
                  {t('addPatient.sex', 'Geschlecht')}
                </FormLabel>
                <ToggleButtonGroup
                  value={patientData.sex}
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue !== null) {
                      handlePatientDataChange('sex', newValue);
                    }
                  }}
                  aria-label={t('addPatient.sex', 'Geschlecht')}
                  sx={{ width: '100%' }}
                >
                  <ToggleButton 
                    value="männlich" 
                    aria-label="männlich"
                    sx={{
                      flex: 1,
                      color: 'grey.500',
                      borderColor: 'grey.300',
                      '&.Mui-selected': {
                        color: 'var(--rd-primary) !important',
                        backgroundColor: 'color-mix(in srgb, var(--rd-primary) 8%, transparent) !important',
                        borderColor: 'var(--rd-primary) !important',
                        '&:hover': {
                          backgroundColor: 'color-mix(in srgb, var(--rd-primary) 15%, transparent) !important'
                        }
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <MaleIcon />
                      <Typography variant="caption">{t('addPatient.male', 'Männlich')}</Typography>
                    </Box>
                  </ToggleButton>
                  <ToggleButton 
                    value="weiblich" 
                    aria-label="weiblich"
                    sx={{
                      flex: 1,
                      color: 'grey.500',
                      borderColor: 'grey.300',
                      '&.Mui-selected': {
                        color: 'var(--rd-primary) !important',
                        backgroundColor: 'color-mix(in srgb, var(--rd-primary) 8%, transparent) !important',
                        borderColor: 'var(--rd-primary) !important',
                        '&:hover': {
                          backgroundColor: 'color-mix(in srgb, var(--rd-primary) 15%, transparent) !important'
                        }
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <FemaleIcon />
                      <Typography variant="caption">{t('addPatient.female', 'Weiblich')}</Typography>
                    </Box>
                  </ToggleButton>
                  <ToggleButton 
                    value="divers" 
                    aria-label="divers"
                    sx={{
                      flex: 1,
                      color: 'grey.500',
                      borderColor: 'grey.300',
                      '&.Mui-selected': {
                        color: 'var(--rd-primary) !important',
                        backgroundColor: 'color-mix(in srgb, var(--rd-primary) 8%, transparent) !important',
                        borderColor: 'var(--rd-primary) !important',
                        '&:hover': {
                          backgroundColor: 'color-mix(in srgb, var(--rd-primary) 15%, transparent) !important'
                        }
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <TransgenderIcon />
                      <Typography variant="caption">{t('addPatient.diverse', 'Divers')}</Typography>
                    </Box>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            
            </Box>
          </Paper>
        </Grid>

        {/* Right Column - Exam Data */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 3, color: 'var(--rd-primary)', fontWeight: 'bold' }}>
              {t('addPatient.examData', 'Untersuchungsdaten')}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Mandant, Zuweiser* */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                {tenants.length > 1 ? (
                  <Autocomplete
                    freeSolo
                    options={tenants}
                    value={tenants.find(t => t.id.toString() === examData.tenant) || null}
                    onChange={(e, newValue) => {
                      if (newValue && newValue.id) {
                        handleExamDataChange('tenant', newValue.id.toString());
                      }
                    }}
                    getOptionLabel={(option) => option?.bezeichnung || ''}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    sx={{ flex: 1 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('addPatient.tenant', 'Mandant')}
                        className="textfield"
                      />
                    )}
                  />
                ) : null}
                <Autocomplete
                  freeSolo
                  options={referringPhysicians}
                  value={examData.referringPhysician}
                  onChange={(e, newValue) => handleExamDataChange('referringPhysician', newValue || '')}
                  onInputChange={(e, newValue) => handleExamDataChange('referringPhysician', newValue)}
                  sx={{ flex: 2 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('addPatient.referringPhysician', 'Zuweiser')}
                      required
                      className="textfield"
                    />
                  )}
                />
              </Box>
              
              {/* Untersuchungsdatum* */}
              <CustomDatePicker
                label={t('addPatient.observationDate', 'Untersuchungsdatum')}
                value={examData.observationDate}
                onChange={(e) => handleExamDataChange('observationDate', e.target.value)}
                required
                sx={{ 
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    minHeight: '40px',
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8.5px 14px',
                    height: '23px',
                  }
                }}
              />
              
              {/* Accession Number, Aufnahme ID* */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label={t('addPatient.accessionNumber', 'Accession Number')}
                  value={examData.accessionNumber}
                  onChange={(e) => handleExamDataChange('accessionNumber', e.target.value)}
                  required
                  className="textfield"
                />
                <TextField
                  fullWidth
                  label={t('addPatient.admissionId', 'Aufnahme-ID')}
                  value={examData.admissionId}
                  onChange={(e) => handleExamDataChange('admissionId', e.target.value)}
                  required
                  className="textfield"
                />
              </Box>
              
              {/* Modalität, Studienbeschreibung* */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Autocomplete
                  freeSolo
                  options={modalities}
                  value={examData.modality}
                  onChange={(e, newValue) => handleExamDataChange('modality', newValue || '')}
                  onInputChange={(e, newValue) => handleExamDataChange('modality', newValue)}
                  sx={{ flex: 1 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('addPatient.modality', 'Modalität')}
                      required
                      className="textfield"
                    />
                  )}
                />
                <Autocomplete
                  freeSolo
                  options={studyDescriptions}
                  value={examData.studyDescription}
                  onChange={(e, newValue) => handleExamDataChange('studyDescription', newValue || '')}
                  onInputChange={(e, newValue) => handleExamDataChange('studyDescription', newValue)}
                  sx={{ flex: 2 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('addPatient.studyDescription', 'Studienbeschreibung')}
                      required
                      className="textfield"
                    />
                  )}
                />
              </Box>
              
              {/* Klinische Informationen */}
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('addPatient.clinicalInfo', 'Klinische Informationen')}
                value={examData.clinicalInfo}
                onChange={(e) => handleExamDataChange('clinicalInfo', e.target.value)}
                className="textfield"
              />
              
              {/* Fragestellung */}
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('addPatient.question', 'Fragestellung')}
                value={examData.question}
                onChange={(e) => handleExamDataChange('question', e.target.value)}
                className="textfield"
              />
              
              {/* Erstbefunder */}
              <Autocomplete
                freeSolo
                options={users}
                value={examData.firstReader}
                onChange={(e, newValue) => {
                  // Handle both object selection and free text input
                  if (typeof newValue === 'string') {
                    handleExamDataChange('firstReader', { id: null, label: newValue });
                  } else {
                    handleExamDataChange('firstReader', newValue);
                  }
                }}
                onInputChange={(e, newValue, reason) => {
                  // Handle free text input
                  if (reason === 'input') {
                    handleExamDataChange('firstReader', newValue ? { id: null, label: newValue } : null);
                  }
                }}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option?.label || '';
                }}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  return option.id === value.id;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('addPatient.firstReader', 'Erstbefunder')}
                    className="textfield"
                  />
                )}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
        <Button
          onClick={() => navigate(-1)}
          className="button-cancel"
          disabled={loading}
        >
          {t('common.cancel', 'Abbrechen')}
        </Button>
        
        <Button
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          className="button"
        >
          {t('addPatient.create', 'Patient anlegen')}
        </Button>
      </Box>
    </Box>
  );
};

export default AddPatient;